export type Bilingual = { en: string; es: string };

export interface HelpFaq {
  q: Bilingual;
  a: Bilingual;
}

export interface HelpCategory {
  id: string;
  title: Bilingual;
  blurb: Bilingual;
  faqs: HelpFaq[];
}

export const HELP_UPDATED = "July 13, 2026";

/**
 * English rewritten for ~8th-grade reading level (short sentences, common
 * words, active voice). Spanish drafted for AI-assisted review — neutral
 * US Spanish, "usted" register. All ES strings pending human review.
 */
export const HELP_CATEGORIES: HelpCategory[] = [
  {
    id: "getting-started",
    title: { en: "Getting started", es: "Para empezar" },
    blurb: {
      en: "What emrkt.co is, who it is for, and how to sign up.",
      es: "Qué es emrkt.co, para quién es y cómo registrarse.",
    },
    faqs: [
      {
        q: {
          en: "What is The Emerging Market (emrkt.co)?",
          es: "¿Qué es The Emerging Market (emrkt.co)?",
        },
        a: {
          en: "It is a website for Alabama communities. We track state bills, grants, local ideas, and partner groups in one place. Small, community-rooted groups can find money, policy news, and each other here.",
          es: "Es un sitio web para las comunidades de Alabama. Seguimos proyectos de ley estatales, subvenciones, ideas locales y grupos aliados en un solo lugar. Los grupos pequeños y comunitarios pueden encontrar aquí fondos, noticias de política y a otros aliados.",
        },
      },
      {
        q: {
          en: "Who is it for?",
          es: "¿Para quién es?",
        },
        a: {
          en: "Small businesses and nonprofits in Alabama. It is also for advocates, agencies, policymakers, and funders who work with them. Signing up is free. Paid plans add research tools and reports.",
          es: "Para pequeñas empresas y organizaciones sin fines de lucro en Alabama. También es para defensores, agencias, legisladores y financiadores que trabajan con ellos. Registrarse es gratis. Los planes de pago añaden herramientas de investigación e informes.",
        },
      },
      {
        q: { en: "How do I create an account?", es: "¿Cómo creo una cuenta?" },
        a: {
          en: "Click Sign in at the top right. Choose email or Google. Confirm the link we send you. Then follow the short checklist on your profile.",
          es: "Haga clic en \"Sign in\" en la parte superior derecha. Elija correo electrónico o Google. Confirme el enlace que le enviamos. Luego siga la breve lista de tareas en su perfil.",
        },
      },
      {
        q: { en: "Is it free to use?", es: "¿Es gratis?" },
        a: {
          en: "Yes for the Member plan. That includes the bill tracker, ideas board, community news, partner list, and basic grant search. Member Pro is $99 per month and adds exports and deeper funder pages. See /pricing for the Intelligence plan for institutions.",
          es: "Sí, el plan Member es gratis. Incluye el seguimiento de leyes, el tablero de ideas, noticias comunitarias, el directorio de aliados y búsqueda básica de subvenciones. Member Pro cuesta $99 al mes y añade exportaciones y perfiles de financiadores. Vea /pricing para el plan Intelligence para instituciones.",
        },
      },
    ],
  },
  {
    id: "ideas",
    title: { en: "Ideas board", es: "Tablero de ideas" },
    blurb: {
      en: "Share an idea, get feedback, and connect with partners.",
      es: "Comparta una idea, reciba comentarios y conecte con aliados.",
    },
    faqs: [
      {
        q: { en: "How do I submit an idea?", es: "¿Cómo comparto una idea?" },
        a: {
          en: "Go to /ideas and click Share an idea. Pick a focus area, write a short description, and tag your county and the help you need. Ideas are public by default so partners can find you.",
          es: "Vaya a /ideas y haga clic en \"Share an idea\". Elija un área de enfoque, escriba una descripción corta y marque su condado y el tipo de ayuda que necesita. Las ideas son públicas por defecto para que los aliados le encuentren.",
        },
      },
      {
        q: { en: "What happens after I post?", es: "¿Qué pasa después de publicar?" },
        a: {
          en: "Your idea shows up on the board and in category and county feeds. Other members can follow it, comment, vote, or ask to partner. You get a notice when someone reacts.",
          es: "Su idea aparece en el tablero y en las listas por categoría y condado. Otros miembros pueden seguirla, comentar, votar o pedir colaborar. Recibirá un aviso cuando alguien participe.",
        },
      },
      {
        q: { en: "How do partner matches work?", es: "¿Cómo funcionan las conexiones con aliados?" },
        a: {
          en: "A partner clicks Express Interest. We send the request through the partner's inbox. You approve or decline before anyone shares contact info. See the Partner Playbook for the full flow.",
          es: "Un aliado hace clic en \"Express Interest\". Enviamos la solicitud a su bandeja de entrada. Usted acepta o rechaza antes de compartir cualquier dato de contacto. Vea el Partner Playbook para todos los detalles.",
        },
      },
      {
        q: { en: "Can I edit or delete an idea?", es: "¿Puedo editar o borrar una idea?" },
        a: {
          en: "Yes. Open the idea from your profile and use the menu. Deleting is final. Edits keep the same URL.",
          es: "Sí. Abra la idea desde su perfil y use el menú. Borrar es permanente. Al editar, el enlace no cambia.",
        },
      },
    ],
  },
  {
    id: "grants",
    title: { en: "Grants & opportunities", es: "Subvenciones y oportunidades" },
    blurb: {
      en: "Find grants, save them, and get deadline reminders.",
      es: "Encuentre subvenciones, guárdelas y reciba avisos de plazos.",
    },
    faqs: [
      {
        q: { en: "Where do the grants come from?", es: "¿De dónde vienen las subvenciones?" },
        a: {
          en: "Our editors curate grant programs from IRS 990 filings, state and federal feeds, and partner submissions. The live search on /opportunities also pulls new listings on demand.",
          es: "Nuestro equipo editorial cura programas de subvenciones desde declaraciones IRS 990, listas estatales y federales, y envíos de aliados. La búsqueda en vivo en /opportunities también trae nuevos anuncios cuando los pida.",
        },
      },
      {
        q: { en: "How do I save a grant?", es: "¿Cómo guardo una subvención?" },
        a: {
          en: "Click the bookmark icon on any grant card. Saved grants appear on your profile under Saved. You can filter by deadline or focus area.",
          es: "Haga clic en el ícono de marcador en cualquier tarjeta de subvención. Las subvenciones guardadas aparecen en su perfil, bajo \"Saved\". Puede filtrar por fecha límite o área de enfoque.",
        },
      },
      {
        q: { en: "Will I get deadline reminders?", es: "¿Recibiré avisos de plazos?" },
        a: {
          en: "Yes. If a saved grant matches a focus area on your profile, we send in-app notices 7 days and 1 day before the deadline. We also include it in the weekly grants email.",
          es: "Sí. Si una subvención guardada coincide con un área de enfoque en su perfil, enviamos avisos dentro de la aplicación 7 días y 1 día antes del plazo. También la incluimos en el correo semanal de subvenciones.",
        },
      },
      {
        q: { en: "How current is the data?", es: "¿Qué tan actualizada está la información?" },
        a: {
          en: "Curated programs refresh every week. Live search runs when you ask. Every listing links to its source. See /methodology for the refresh schedule per dataset.",
          es: "Los programas curados se actualizan cada semana. La búsqueda en vivo se ejecuta al momento. Cada anuncio enlaza a su fuente. Vea /methodology para el calendario de actualización de cada conjunto de datos.",
        },
      },
    ],
  },
  {
    id: "partners",
    title: { en: "Partners & affiliates", es: "Aliados y afiliados" },
    blurb: {
      en: "Apply to the network, sign an MOU, and join the referral program.",
      es: "Solicite unirse a la red, firme un MOU y participe en el programa de referidos.",
    },
    faqs: [
      {
        q: { en: "How do I become a partner?", es: "¿Cómo me convierto en aliado?" },
        a: {
          en: "Apply at /partners/apply. We review your fit against the community-benefit standard in the Partner Playbook. Approved partners get a directory listing, an MOU to sign, and access to PartnerHub.",
          es: "Solicítelo en /partners/apply. Revisamos si cumple con el estándar de beneficio comunitario en el Partner Playbook. Los aliados aprobados reciben una ficha en el directorio, un MOU para firmar y acceso a PartnerHub.",
        },
      },
      {
        q: { en: "What is the difference between Advocate, Partner, and Anchor?", es: "¿Cuál es la diferencia entre Advocate, Partner y Anchor?" },
        a: {
          en: "Advocates share the site with a referral link and earn a share of new subscriptions. Partners offer paid services to leads we route to them. Anchors are big institutional partners that make long-term commitments. See the Partner Playbook for full detail.",
          es: "Los Advocates comparten el sitio con un enlace de referido y ganan una parte de las nuevas suscripciones. Los Partners ofrecen servicios pagados a los contactos que les enviamos. Los Anchors son aliados institucionales grandes que asumen compromisos a largo plazo. Vea el Partner Playbook para más detalles.",
        },
      },
      {
        q: { en: "Where is the referral program?", es: "¿Dónde está el programa de referidos?" },
        a: {
          en: "Go to /affiliates. Once approved, your dashboard shows your link, clicks, leads, and payout status.",
          es: "Vaya a /affiliates. Una vez aprobado, su panel mostrará su enlace, clics, contactos y estado de pagos.",
        },
      },
      {
        q: { en: "Where is the Partner Field Guide?", es: "¿Dónde está la Partner Field Guide?" },
        a: {
          en: "Download the PDF at /handbook/emrkt-partner-field-guide.pdf. Or read it in the app at /partners/handbook.",
          es: "Descargue el PDF en /handbook/emrkt-partner-field-guide.pdf. O léalo en la aplicación en /partners/handbook.",
        },
      },
    ],
  },
  {
    id: "account",
    title: { en: "Account & billing", es: "Cuenta y pagos" },
    blurb: {
      en: "Manage your profile, your subscription, and your login security.",
      es: "Gestione su perfil, su suscripción y la seguridad de su inicio de sesión.",
    },
    faqs: [
      {
        q: { en: "How do I upgrade to Pro?", es: "¿Cómo cambio al plan Pro?" },
        a: {
          en: "Go to /pricing and choose Pro. Our billing provider handles checkout. Your plan starts as soon as payment clears.",
          es: "Vaya a /pricing y elija Pro. Nuestro procesador de pagos maneja el cobro. Su plan se activa en cuanto se procese el pago.",
        },
      },
      {
        q: { en: "How do I cancel or change plans?", es: "¿Cómo cancelo o cambio mi plan?" },
        a: {
          en: "Open /account/billing and use Manage subscription. Cancellations end at the close of your current billing period. You keep Pro access until then.",
          es: "Abra /account/billing y use \"Manage subscription\". La cancelación surte efecto al final de su período de facturación actual. Mantiene el acceso Pro hasta entonces.",
        },
      },
      {
        q: { en: "How do I turn on two-factor sign-in?", es: "¿Cómo activo el inicio de sesión de dos pasos?" },
        a: {
          en: "Go to /account and turn on Two-factor authentication under Security. We support authenticator apps. You get recovery codes at setup — save them.",
          es: "Vaya a /account y active \"Two-factor authentication\" bajo Security. Admitimos apps de autenticación. Al configurarlo recibirá códigos de recuperación — guárdelos.",
        },
      },
      {
        q: { en: "How do I delete my account?", es: "¿Cómo elimino mi cuenta?" },
        a: {
          en: "Open /account and choose Delete account. You can anonymize your posts or delete everything. See /retention to learn what happens to each kind of data.",
          es: "Abra /account y elija \"Delete account\". Puede anonimizar sus publicaciones o borrar todo. Vea /retention para saber qué pasa con cada tipo de dato.",
        },
      },
    ],
  },
  {
    id: "privacy",
    title: { en: "Privacy, security & data", es: "Privacidad, seguridad y datos" },
    blurb: {
      en: "How we store, protect, and use your data.",
      es: "Cómo guardamos, protegemos y usamos sus datos.",
    },
    faqs: [
      {
        q: { en: "How is my data protected?", es: "¿Cómo se protegen mis datos?" },
        a: {
          en: "The site runs on a managed database with strict access rules on every table. Data is encrypted in transit and backups are encrypted. See /security for our full commitments.",
          es: "El sitio funciona con una base de datos gestionada con reglas de acceso estrictas en cada tabla. Los datos se cifran en tránsito y las copias de seguridad están cifradas. Vea /security para todos los compromisos.",
        },
      },
      {
        q: { en: "Do you sell my data?", es: "¿Venden mis datos?" },
        a: {
          en: "No. We do not sell personal data. We may publish research using grouped, anonymous data. You choose whether your posts can be part of that research on your profile.",
          es: "No. No vendemos datos personales. Podemos publicar investigaciones con datos agrupados y anónimos. Usted decide en su perfil si sus publicaciones pueden usarse en esa investigación.",
        },
      },
      {
        q: { en: "How do I export or delete my data?", es: "¿Cómo exporto o elimino mis datos?" },
        a: {
          en: "Email info@emrkt.co to export your data. You can delete your account yourself at /account. See /retention for retention windows.",
          es: "Escriba a info@emrkt.co para exportar sus datos. Puede eliminar su cuenta usted mismo en /account. Vea /retention para los plazos de retención.",
        },
      },
      {
        q: { en: "Who do I contact about a security issue?", es: "¿Con quién contacto sobre un problema de seguridad?" },
        a: {
          en: "Email info@emrkt.co with the subject \"Security\" and steps to reproduce the issue. We aim to notify affected users within 72 hours of a breach. See /security and /incident-response.",
          es: "Escriba a info@emrkt.co con el asunto \"Security\" y los pasos para reproducir el problema. Nuestra meta es avisar a los usuarios afectados en 72 horas tras una brecha. Vea /security y /incident-response.",
        },
      },
    ],
  },
  {
    id: "contact",
    title: { en: "Still stuck?", es: "¿Aún necesita ayuda?" },
    blurb: {
      en: "How to reach a real person.",
      es: "Cómo hablar con una persona real.",
    },
    faqs: [
      {
        q: { en: "How do I contact support?", es: "¿Cómo contacto a soporte?" },
        a: {
          en: "Email info@emrkt.co. We aim to reply within 2 business days. We handle accessibility and security reports first.",
          es: "Escriba a info@emrkt.co. Nuestra meta es responder en 2 días hábiles. Priorizamos los reportes de accesibilidad y seguridad.",
        },
      },
      {
        q: { en: "Where do I report an accessibility barrier?", es: "¿Dónde reporto una barrera de accesibilidad?" },
        a: {
          en: "Email info@emrkt.co with the subject \"Accessibility\". Include the page URL, your device and browser, and what happened. Full detail at /accessibility.",
          es: "Escriba a info@emrkt.co con el asunto \"Accessibility\". Incluya el enlace de la página, su dispositivo y navegador, y lo que ocurrió. Más detalles en /accessibility.",
        },
      },
      {
        q: { en: "How do I report bad content or a policy violation?", es: "¿Cómo reporto contenido inapropiado o una violación de política?" },
        a: {
          en: "Use the Report link on any idea, comment, or profile. Or email info@emrkt.co. Our review standard is at /acceptable-use.",
          es: "Use el enlace \"Report\" en cualquier idea, comentario o perfil. O escriba a info@emrkt.co. Nuestro estándar de revisión está en /acceptable-use.",
        },
      },
      {
        q: { en: "Is there a status page?", es: "¿Hay una página de estado?" },
        a: {
          en: "Yes. /status shows current uptime and any active incidents.",
          es: "Sí. /status muestra el tiempo de actividad actual y cualquier incidente activo.",
        },
      },
    ],
  },
];